'use client';

import { useEffect, useState } from 'react';
import { Bell, X, Settings } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

// Only set this key when user explicitly clicks "Jetzt nicht" (dismiss)
// NOT when they grant permission — so the prompt can re-appear if needed
const PUSH_DISMISSED_KEY = 'winewood_push_dismissed';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function isPushSupported(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  // On iOS PWA, Notification may exist but only after SW is ready
  // We check for it but don't hard-fail if it's missing at this point
  return true;
}

export default function PushPermissionPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isPushSupported()) return;
    if (!user) return;

    // Don't show if user already explicitly dismissed
    const dismissed = localStorage.getItem(PUSH_DISMISSED_KEY);
    if (dismissed) return;

    // Check current permission state
    // On iOS PWA, Notification.permission starts as 'default' until the user is asked
    const checkAndShow = () => {
      // If Notification API is available and already decided, don't show
      if ('Notification' in window && Notification.permission !== 'default') return;

      // Show the prompt after a short delay so the app loads first
      const timer = setTimeout(() => setVisible(true), 2500);
      return timer;
    };

    const timer = checkAndShow();
    return () => { if (timer) clearTimeout(timer); };
  }, [user]);

  const handleAllow = async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      // Register SW first — required on iOS before Notification.requestPermission
      const swReg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      console.log('[PushPrompt] SW ready');

      // On iOS PWA, Notification.requestPermission must be called from a user gesture
      // This function is called directly from a button click, so it qualifies
      let permission: NotificationPermission = 'default';
      if ('Notification' in window) {
        permission = await Notification.requestPermission();
      }
      console.log('[PushPrompt] Notification.permission after request:', permission);

      if (permission === 'granted') {
        // Fetch VAPID public key at runtime from server so Railway env vars are used
        const res = await fetch('/api/push/vapid-public-key');
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `VAPID key endpoint returned ${res.status}`);
        }
        const { publicKey: vapidKey } = await res.json();
        console.log('[PushPrompt] VAPID public key received, length:', vapidKey?.length);

        const existing = await swReg.pushManager.getSubscription();
        console.log('[PushPrompt] Existing subscription:', existing ? 'yes' : 'none');
        const sub = existing || await swReg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
        const subJson = sub.toJSON();
        console.log('[PushPrompt] Subscription endpoint:', subJson.endpoint?.slice(0, 80) + '...');
        console.log('[PushPrompt] Subscription keys present:', !!subJson.keys);

        if (user) {
          console.log('[PushPrompt] Saving subscription for userId:', user.id);
          const saveRes = await fetch('/api/push/subscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId: user.id, subscription: subJson }),
          });
          const saveData = await saveRes.json().catch(() => ({}));
          console.log('[PushPrompt] Save response status:', saveRes.status, '| body:', JSON.stringify(saveData));
          if (!saveRes.ok) {
            throw new Error(saveData.error || `Subscription save failed (${saveRes.status})`);
          }
        }
        // Permission granted — close prompt without setting dismissed key
        // so if subscription ever expires, the prompt can show again
        setVisible(false);
      } else {
        // User denied in the system dialog — mark as dismissed so we don't keep asking
        localStorage.setItem(PUSH_DISMISSED_KEY, '1');
        setVisible(false);
      }
    } catch (err: any) {
      console.error('[PushPrompt] Error during activation:', err?.message, err);
      setErrorMsg(err?.message || 'Aktivierung fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    // Only set dismissed key when user explicitly clicks "Jetzt nicht"
    localStorage.setItem(PUSH_DISMISSED_KEY, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9980]"
        style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
        onClick={handleDismiss}
      />

      {/* Bottom sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9981] rounded-t-[24px] px-5 pt-5 pb-8"
        style={{
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderBottom: 'none',
          animation: 'pushPromptSlideUp 0.35s cubic-bezier(0.34,1.2,0.64,1) forwards',
          maxWidth: '430px',
          margin: '0 auto',
        }}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 rounded-full mx-auto mb-5" style={{ background: 'var(--border)' }} />

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-5 right-5 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ background: 'var(--muted)' }}
          aria-label="Schließen"
        >
          <X size={14} color="var(--muted-foreground)" />
        </button>

        {/* Icon */}
        <div className="flex flex-col items-center mb-5">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
            style={{ background: 'rgba(124,179,66,0.15)', border: '2px solid rgba(124,179,66,0.3)' }}
          >
            <Bell size={26} color="var(--primary)" />
          </div>
          <h2 className="text-lg font-bold text-center" style={{ color: 'var(--card-foreground)' }}>
            Bleib immer informiert 🔔
          </h2>
          <p className="text-sm text-center mt-2 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Erhalte Push-Benachrichtigungen für neue Nachrichten, Wartelisten-Updates und exklusive Angebote.
          </p>
        </div>

        {/* Hint */}
        <div
          className="flex items-start gap-2.5 rounded-app px-3 py-2.5 mb-4"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <Settings size={13} color="var(--muted-foreground)" className="flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Du kannst Push-Benachrichtigungen jederzeit in deinem{' '}
            <span style={{ color: 'var(--card-foreground)', fontWeight: 600 }}>Profil → Einstellungen</span>{' '}
            ändern oder deaktivieren.
          </p>
        </div>

        {/* Allow button */}
        <button
          onClick={handleAllow}
          disabled={loading}
          className="w-full py-3.5 rounded-pill text-sm font-bold transition-all duration-150 active:scale-[0.98] disabled:opacity-70 mb-3 flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)', color: '#fff' }}
        >
          {loading ? (
            <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : (
            <Bell size={15} />
          )}
          {loading ? 'Wird aktiviert...' : 'Benachrichtigungen aktivieren'}
        </button>

        {/* Error message */}
        {errorMsg && (
          <div
            className="w-full rounded-app px-3 py-2 mb-3 text-xs text-center"
            style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}
          >
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Dismiss link */}
        <button
          onClick={handleDismiss}
          className="w-full text-center text-xs py-1 transition-colors duration-150"
          style={{ color: 'var(--muted-foreground)' }}
        >
          Jetzt nicht
        </button>
      </div>

      <style>{`
        @keyframes pushPromptSlideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}
